import { useState } from 'react';

type TileState = 'loading' | 'loaded' | 'error';

interface GameGalleryTileProps {
  /** Gallery image (400×225 WebP under /games/<folder>/). */
  src: string;
  /** The game's icon: soft placeholder while the image loads, and the fallback when it fails. */
  iconSrc?: string;
}

const TILE = 'relative aspect-[178/105] min-w-0 overflow-hidden rounded-control border border-edge-subtle bg-inset';

/**
 * One game-sheet gallery tile. The icon (already on screen in the game list, so usually cached)
 * sits behind the image, blurred, and the tile pulses until the image arrives and fades in. On a
 * slow line the tile reads as loading (even before the icon is there); when the image fails — offline and not cached yet, or a
 * missing file (the host answers with the app's HTML) — the tile settles on the plain icon.
 * Remount per src (key) so switching games starts from the placeholder again.
 */
export function GameGalleryTile({ src, iconSrc }: GameGalleryTileProps) {
  const [state, setState] = useState<TileState>('loading');

  return (
    <div
      className={state === 'loading' ? `${TILE} animate-pulse motion-reduce:animate-none` : TILE}
      data-testid="game-sheet-gallery-tile"
      data-state={state}
    >
      {iconSrc ? (
        <img
          src={iconSrc}
          alt=""
          aria-hidden
          data-testid="game-sheet-gallery-placeholder"
          className={
            state === 'error'
              ? 'absolute inset-0 h-full w-full object-cover opacity-90'
              : 'absolute inset-0 h-full w-full scale-110 object-cover opacity-50 blur-sm'
          }
        />
      ) : null}
      {state !== 'error' ? (
        <img
          src={src}
          alt=""
          width={400}
          height={225}
          decoding="async"
          data-testid="game-sheet-gallery-image"
          ref={(img) => {
            // Already in the HTTP / service-worker cache: it may have finished before React listened.
            if (img?.complete && img.naturalWidth > 0) setState((s) => (s === 'loading' ? 'loaded' : s));
          }}
          onLoad={() => setState('loaded')}
          onError={() => setState('error')}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 motion-reduce:transition-none ${
            state === 'loaded' ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ) : null}
    </div>
  );
}
